import React, { useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import {
  nameForCategorySlug,
  slugFromCategoryName,
  TRANSACTION_CATEGORY_OPTIONS,
} from '../../config/spendCategories';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { GlassSurface } from '../ui/glass';
import { GlassButton } from '../ui/GlassButton';

export type TransactionEditDraft = {
  categorySlug: string;
  categoryName: string;
  hiddenFromHousehold: boolean;
};

export type TransactionEditTarget = {
  id: string;
  merchant: string;
  categoryName?: string;
  hiddenFromHousehold?: boolean;
};

type Props = {
  visible: boolean;
  initial: TransactionEditTarget | null;
  saving?: boolean;
  onClose: () => void;
  onSave: (draft: TransactionEditDraft) => void;
};

/** Web centered dialog to recategorize a transaction and toggle household visibility. */
export function EditTransactionModal({
  visible,
  initial,
  saving = false,
  onClose,
  onSave,
}: Props) {
  const [categorySlug, setCategorySlug] = useState('other');
  const [hiddenFromHousehold, setHiddenFromHousehold] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setCategorySlug(slugFromCategoryName(initial?.categoryName));
    setHiddenFromHousehold(initial?.hiddenFromHousehold === true);
  }, [visible, initial]);

  const handleSave = () => {
    onSave({
      categorySlug,
      categoryName: nameForCategorySlug(categorySlug),
      hiddenFromHousehold,
    });
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Pressable
          style={styles.dismissArea}
          onPress={onClose}
          accessibilityLabel="Dismiss"
        />
        <View style={styles.panelWrap} pointerEvents="box-none">
          <GlassSurface
            tint="neutral"
            rounded="xl"
            showHairline
            style={styles.panel}
            contentStyle={styles.panelContent}
          >
            <Text style={styles.title}>Edit transaction</Text>
            {initial?.merchant ? (
              <Text style={styles.merchant} numberOfLines={2}>
                {initial.merchant}
              </Text>
            ) : null}

            <Text style={styles.sectionLabel}>Category</Text>
            <View style={styles.chipRow}>
              {TRANSACTION_CATEGORY_OPTIONS.map((opt) => {
                const selected = opt.slug === categorySlug;
                return (
                  <Pressable
                    key={opt.slug}
                    onPress={() => setCategorySlug(opt.slug)}
                    style={[styles.chip, selected && styles.chipOn]}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    accessibilityLabel={opt.name}
                  >
                    <Text style={[styles.chipText, selected && styles.chipTextOn]}>
                      {opt.name}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <Pressable
              onPress={() => setHiddenFromHousehold((v) => !v)}
              style={styles.hideRow}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: hiddenFromHousehold }}
            >
              <View style={[styles.checkbox, hiddenFromHousehold && styles.checkboxOn]}>
                {hiddenFromHousehold ? <Text style={styles.checkboxMark}>✓</Text> : null}
              </View>
              <View style={styles.hideCopy}>
                <Text style={styles.hideLabel}>Personal only</Text>
                <Text style={styles.hideHint}>Hidden from household view</Text>
              </View>
            </Pressable>

            <View style={styles.actions}>
              <GlassButton variant="ghost" size="sm" onPress={onClose} disabled={saving}>
                Cancel
              </GlassButton>
              <GlassButton
                variant="primary"
                size="sm"
                onPress={handleSave}
                disabled={saving}
                accessibilityLabel="Save"
              >
                {saving ? 'Saving…' : 'Save'}
              </GlassButton>
            </View>
          </GlassSurface>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(2, 6, 23, 0.72)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  dismissArea: {
    ...StyleSheet.absoluteFillObject,
  },
  panelWrap: {
    width: '100%',
    maxWidth: 440,
    zIndex: 1,
  },
  panel: {
    width: '100%',
    borderRadius: radii.xl,
  },
  panelContent: {
    padding: spacing.lg,
    gap: spacing.sm,
  },
  title: {
    ...typography.headline,
    fontSize: 18,
    color: colors.text,
  },
  merchant: {
    ...typography.body,
    color: colors.textMuted,
  },
  sectionLabel: {
    ...typography.caption,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: spacing.xs,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    paddingVertical: 2,
  },
  chip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 8,
    borderRadius: radii.pill,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  chipOn: {
    borderColor: colors.accent,
    backgroundColor: 'rgba(61, 214, 164, 0.16)',
  },
  chipText: {
    ...typography.caption,
    color: colors.textMuted,
    fontWeight: '600',
  },
  chipTextOn: {
    color: colors.accent,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    paddingTop: spacing.xs,
  },
  hideRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.04)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxOn: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
  },
  checkboxMark: {
    color: colors.background,
    fontSize: 12,
    fontWeight: '800',
    lineHeight: 14,
  },
  hideCopy: {
    flex: 1,
    gap: 2,
  },
  hideLabel: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  hideHint: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 12,
  },
});
